import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  Image,
  Link as ChakraLink,
  SimpleGrid,
  Skeleton,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  MdAssignment,
  MdCalendarMonth,
  MdDescription,
  MdHomeWork,
  MdMenuBook,
  MdPlayCircle,
  MdQuiz,
  MdVideoLibrary,
} from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import StudentPortalShell from "../../../components/student/StudentPortalShell";
import { studentPalette, studentInk } from "../../../constants/studentPalette";
import {
  studentOutlineButtonDarkSx,
  studentSurfaces,
} from "../../../constants/studentThemeSurfaces";
import CourseVideoModal from "../../../components/student/CourseVideoModal";
import PdfPreviewModal, {
  homeworkSubmissionPdfUrl,
  lessonPdfPublicUrl,
} from "../../../components/student/PdfPreviewModal";
import AddVoucher from "../../../containers/student/models/add-voucher";
import SolveHomework from "../../../containers/student/models/solve-homework";
import { countSeens } from "../../../containers/student/video/func";
import { getChapterContentRequest } from "../../../modules/students-modules/chapters/Actions";
import { openAddVoucherModel, openSolveHomeworkModel } from "../../../modules/admin-modules/models/Actions";
import { getAllSectionsRequest, getSectionRequest } from "../../../modules/students-modules/sections/Actions";
import { examRedoRequest } from "../../../modules/students-modules/exams/Actions";
import {
  moreVideoViewsRequest,
  setLiveVideoSeenRequest,
} from "../../../modules/students-modules/videos/Actions";

const C = {
  ...studentPalette,
  border: studentSurfaces.border,
  paper: studentSurfaces.raised,
  sand: studentSurfaces.canvas,
  muted: studentSurfaces.textMuted,
  tertiary: studentInk,
};

function publicAssetUrl(path) {
  if (!path) return null;
  if (String(path).startsWith("http")) return path;
  const raw = process.env.REACT_APP_API_URL || "https://api.edutopiaonline.com/";
  const base = String(raw).replace(/\s/g, "").replace(/['"]/g, "").replace(/\/$/, "");
  const clean = String(path).replace(/^\//, "");
  return `${base}/public/${clean}`;
}

function itemTitle(item) {
  return item?.title || item?.name || "Untitled";
}

function videoAlertMessage(v) {
  const vc = v?.views_count?.views_count;
  if (vc === 3) return "Please contact technical support";
  if (vc === 4) {
    return "You have exceeded the allowed watch duration. You can request a voucher and contact your assistant.";
  }
  if (v?.suspended === 1 && v?.user_video_suspended !== 0) {
    if (v?.related_to_homework === 1 && v?.exams?.length) {
      return `Video locked — submit homework or complete the exam ${v.exams[0]?.name}`;
    }
    if (v?.related_to_homework === 1 && !v?.exams?.length) {
      return "Video locked — please submit homework";
    }
    if (v?.related_to_homework === 0 && v?.exams?.length) {
      return `Video locked — please complete the exam ${v.exams[0]?.name}`;
    }
    return "Video locked — request a voucher and contact your assistant";
  }
  return "";
}

function needsVoucherActions(v) {
  const used = v?.views_count?.views_count ?? 0;
  const max = v?.max_allowed_views ?? 0;
  const overOrEqual = max > 0 && max <= used;
  const locked = v?.suspended == 1 && v?.user_video_suspended != 0;
  return overOrEqual || locked;
}

function formatMetaLine(item, kind) {
  if (kind === "video") {
    const dur = item?.duration || item?.video_duration || item?.length;
    const d = dur != null ? `${dur}${String(dur).includes(":") ? "" : " min"}` : "";
    return d ? `Video session • ${d}` : "Video session";
  }
  if (kind === "lesson") {
    const sz = item?.size || item?.file_size;
    return sz ? `PDF • ${sz}` : "PDF document";
  }
  if (kind === "exam") {
    const n =
      item?.total_count ??
      item?.questions_count ??
      item?.questions ??
      item?.question_count ??
      item?.no_of_questions;
    const timed = item?.timer == 1 && item?.time != null && String(item.time).trim() !== "";
    const pts = item?.score;
    const hasPts = pts != null && String(pts).trim() !== "" && Number(pts) > 0;
    const parts = [];
    if (n != null && String(n).trim() !== "") parts.push(`${n} questions`);
    if (timed) parts.push(`${item.time} min`);
    if (hasPts) parts.push(`${pts} pts`);
    return parts.length ? `Exam • ${parts.join(" • ")}` : "Exam";
  }
  if (kind === "live") {
    return item?.subtitle || item?.description || "Live session";
  }
  if (kind === "homework") {
    const d = item?.created_at;
    return d ? `Homework • ${dayjs(d).format("D MMM YYYY")}` : "Homework";
  }
  return "";
}

function homeworkActionLabel(hw) {
  const sh = hw?.student_homework;
  const hasFile = sh?.file;
  const status = sh?.status;
  if ((status === 0 || status === 1) && hasFile) return "Preview";
  if (status === 2) return "Resubmit";
  return "Solve";
}

function homeworkStatusSummary(hw) {
  const sh = hw?.student_homework;
  if (!sh) return null;
  if (sh.status === 0) return "Pending review";
  if (sh.status === 1) return "Accepted";
  if (sh.status === 2) return "Refused — you can resubmit";
  return "Submitted";
}

/** One shell per card so hero + body share a single border (no seam between stacked boxes). */
const CurriculumCard = ({ children }) => (
  <Box
    bg={studentSurfaces.raised}
    borderWidth="1px"
    borderColor={C.border}
    borderRadius="xl"
    overflow="hidden"
    transition="transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease"
    willChange="transform"
    _hover={{
      borderColor: `${C.primary}66`,
      transform: "scale(1.02)",
      boxShadow:
        "0 18px 46px rgba(91, 13, 22, 0.18), 0 0 0 1px rgba(168, 160, 150, 0.35)",
    }}
    _active={{ transform: "scale(1.01)" }}
    display="flex"
    flexDirection="column"
    h="100%"
  >
    {children}
  </Box>
);

const CurriculumCardMedia = ({ children, badge, badgeTone = "primary", topRight, hero }) => (
  <Box
    position="relative"
    h={{ base: "132px", sm: "144px" }}
    overflow="hidden"
    bgGradient={hero || "linear(to-br, rgba(91, 13, 22,0.12), rgba(91, 13, 22,0.06))"}
    display="flex"
    alignItems="center"
    justifyContent="center"
  >
    <Box
      position="absolute"
      top={3}
      left={3}
      bg={
        badgeTone === "exam"
          ? C.secondary
          : badgeTone === "homework"
          ? C.tertiary
          : badgeTone === "pdf"
          ? C.tertiary
          : C.primary
      }
      color="white"
      fontSize="9px"
      fontWeight="800"
      textTransform="uppercase"
      letterSpacing="0.06em"
      px={2}
      py={0.5}
      borderRadius="full"
    >
      {badge}
    </Box>
    {topRight ? (
      <Box position="absolute" top={3} right={3}>
        {topRight}
      </Box>
    ) : null}
    {children}
  </Box>
);

const CurriculumCardBody = ({ children }) => (
  <Box
    p={4}
    borderTopWidth="1px"
    borderTopColor="rgba(216, 206, 195, 0.75)"
    display="flex"
    flexDirection="column"
    flex="1"
    minH="168px"
  >
    {children}
  </Box>
);

const ChapterContentGrid = ({ bucket, chapter, onWatchVideo, userData, courseReturnPath, onOpenPdf }) => {
  const dispatch = useDispatch();
  const voucherLoading = useSelector((s) => s.chapterVideos.voucherLoading);
  const [homeworkForModal, setHomeworkForModal] = useState(null);
  const [redoLoadingExamId, setRedoLoadingExamId] = useState(null);

  if (!bucket || bucket.loading) {
    return (
      <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={4} p={5}>
        {[1, 2, 3].map((k) => (
          <Skeleton key={k} h="200px" borderRadius="xl" />
        ))}
      </Grid>
    );
  }

  if (bucket.error) {
    return (
      <Text p={5} color="red.600" fontWeight="600" fontSize="sm">
        {bucket.error}
      </Text>
    );
  }

  const videos = bucket.videos || [];
  const lessons = bucket.lessons || [];
  const exams = bucket.exams || [];
  const liveVideos = bucket.live_videos || [];
  const homeworks = bucket.homeworks || [];

  const hasAny =
    videos.length +
      lessons.length +
      exams.length +
      liveVideos.length +
      homeworks.length >
    0;

  if (!hasAny) {
    return (
      <Text p={5} color={C.muted} fontWeight="600" fontSize="sm">
        No content in this lesson yet.
      </Text>
    );
  }

  const chapterLabel = (chapter?.name || "Lesson").toUpperCase();

  const handleWatch = (video) => {
    if (video?.suspended != 1) {
      countSeens(dispatch, video);
    }
    onWatchVideo?.(video, chapterLabel);
  };

  const handleHomeworkPrimary = (hw) => {
    const sh = hw?.student_homework;
    const hasFile = sh?.file;
    const status = sh?.status;
    if ((status === 0 || status === 1) && hasFile) {
      const url = homeworkSubmissionPdfUrl(hasFile);
      if (url)
        onOpenPdf?.({
          url,
          title: itemTitle(hw),
          backLabel: "Back to curriculum",
        });
    } else if (status === 2 || !sh) {
      setHomeworkForModal(hw);
      dispatch(openSolveHomeworkModel());
    }
  };

  const homeworkDisabled = (hw) => hw?.active === 0 || hw?.enable === 0;

  return (
    <Box p={5}>
      <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={4}>
        {videos.map((v) => {
          const alert = videoAlertMessage(v);
          const alertColor =
            v?.views_count?.views_count >= 4 ? "#b83232" : `${C.primary}cc`;
          const showVoucherRow = needsVoucherActions(v);
          const showWatchButton = !alert && !showVoucherRow;
          const videoThumbUrl = `https://api.edutopiaonline.com/public/thumbnails/${v?.thumbnail}`;
          const viewsLine =
            v?.max_allowed_views && userData?.subject?.id != "34" && !alert
              ? `Watch ${
                  v.max_allowed_views -
                    (v?.views_count?.views_count > v.max_allowed_views
                      ? v.max_allowed_views
                      : v?.views_count?.views_count || 0) || 0
                } more times (${Math.min(
                  v?.views_count?.views_count ?? 0,
                  v.max_allowed_views
                )}/${v.max_allowed_views})`
              : null;

          return (
            <CurriculumCard key={`v-${v.id}`}>
              <CurriculumCardMedia
                badge="Session"
                badgeTone="primary"
                hero={
                  videoThumbUrl
                    ? "linear(to-br, rgba(0,0,0,0.0), rgba(0,0,0,0.0))"
                    : "linear(to-br, rgba(91, 13, 22,0.15), rgba(91, 13, 22,0.05))"
                }
              >
                {videoThumbUrl ? (
                  <>
                    <Image
                      src={videoThumbUrl}
                      alt={`${itemTitle(v)} thumbnail`}
                      position="absolute"
                      inset={0}
                      w="100%"
                      h="100%"
                      objectFit="cover"
                      objectPosition="center"
                      display="block"
                    />
                    <Box
                      position="absolute"
                      inset={0}
                      bgGradient="linear(to-br, rgba(91, 13, 22,0.25), rgba(91, 13, 22,0.14))"
                    />
                    <Box position="relative" zIndex={1}>
                      <MdPlayCircle color="white" size={52} />
                    </Box>
                  </>
                ) : (
                  <MdPlayCircle color={C.primary} size={48} />
                )}
              </CurriculumCardMedia>
              <CurriculumCardBody>
                <Text fontSize="sm" fontWeight="800" color={C.primary} noOfLines={2} mb={1}>
                  {itemTitle(v)}
                </Text>
                <Text fontSize="10px" fontWeight="700" color={`${C.primary}99`} textTransform="uppercase" letterSpacing="0.06em" mb={2}>
                  {formatMetaLine(v, "video")}
                </Text>
                {alert ? (
                  <Text fontSize="xs" fontWeight="600" color={alertColor} mb={3} lineHeight="1.45">
                    {alert}
                  </Text>
                ) : viewsLine ? (
                  <Text fontSize="11px" fontWeight="600" color={`${C.primary}aa`} mb={3}>
                    {viewsLine}
                  </Text>
                ) : null}
                <Box mt="auto">
                  {showVoucherRow ? (
                    <Flex direction={{ base: "column", sm: "row" }} gap={2} mb={showWatchButton ? 2 : 0}>
                      <Button
                        flex={1}
                        size="sm"
                        bg={C.primary}
                        color="white"
                        fontWeight="800"
                        textTransform="uppercase"
                        fontSize="10px"
                        borderRadius="lg"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const fd = new FormData();
                          fd.append("video_id", v.id);
                          dispatch(moreVideoViewsRequest(fd));
                        }}
                      >
                        Request voucher
                      </Button>
                      <Button
                        flex={1}
                        size="sm"
                        variant="outline"
                        borderWidth="2px"
                        borderColor={C.primary}
                        color={C.primary}
                        fontWeight="800"
                        textTransform="uppercase"
                        fontSize="10px"
                        borderRadius="lg"
                        isLoading={voucherLoading}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          dispatch(openAddVoucherModel(v.id));
                        }}
                      >
                        Add voucher
                      </Button>
                    </Flex>
                  ) : null}
                  {showWatchButton ? (
                    <Button
                      w="full"
                      size="sm"
                      variant="outline"
                      borderWidth="2px"
                      borderColor={C.primary}
                      color={C.primary}
                      fontWeight="800"
                      textTransform="uppercase"
                      fontSize="10px"
                      borderRadius="lg"
                      sx={studentOutlineButtonDarkSx}
                      _hover={{ bg: `${C.primary}12` }}
                      onClick={() => handleWatch(v)}
                    >
                      Watch
                    </Button>
                  ) : null}
                </Box>
              </CurriculumCardBody>
            </CurriculumCard>
          );
        })}

        {lessons.map((pdf) => {
          const pdfUrl = pdf?.file ? lessonPdfPublicUrl(pdf.file) : "";
          return (
            <CurriculumCard key={`l-${pdf.id}`}>
              <CurriculumCardMedia
                badge="PDF"
                badgeTone="pdf"
                hero="linear(to-br, rgba(214,206,192,0.65), rgba(247,242,236,0.95))"
              >
                <MdDescription color={C.primary} size={48} />
              </CurriculumCardMedia>
              <CurriculumCardBody>
                <Text fontSize="sm" fontWeight="800" color={C.primary} noOfLines={2} mb={1}>
                  {itemTitle(pdf)}
                </Text>
                <Text fontSize="10px" fontWeight="700" color={`${C.primary}99`} textTransform="uppercase" letterSpacing="0.06em" mb={3}>
                  {formatMetaLine(pdf, "lesson")}
                </Text>
                <Box mt="auto">
                  <Button
                    w="full"
                    size="sm"
                    bg={C.primary}
                    color="white"
                    fontWeight="800"
                    textTransform="uppercase"
                    fontSize="10px"
                    borderRadius="lg"
                    isDisabled={!pdfUrl}
                    _hover={{ bg: C.secondary }}
                    onClick={() => {
                      if (!pdfUrl) return;
                      onOpenPdf?.({
                        url: pdfUrl,
                        title: itemTitle(pdf),
                        backLabel: "Back to curriculum",
                      });
                    }}
                  >
                    View PDF
                  </Button>
                </Box>
              </CurriculumCardBody>
            </CurriculumCard>
          );
        })}

        {exams.map((ex) => {
          const examGuideQs = new URLSearchParams({ name: itemTitle(ex) });
          if (courseReturnPath) examGuideQs.set("returnTo", courseReturnPath);
          const examResultQs = new URLSearchParams({ name: itemTitle(ex) });
          const examDone = ex?.right_answers != null;
          const canReviewAnswers = examDone && ex?.correction == 1;
          const examResultPath = `/exam-result/${ex.id}?${examResultQs.toString()}`;
          const examGuidePath = `/student/exam-guide/${ex.id}?${examGuideQs.toString()}`;
          const scoreDenom = ex?.score ?? ex?.total_count;
          return (
            <CurriculumCard key={`e-${ex.id}`}>
              <CurriculumCardMedia badge="Exam" badgeTone="exam" hero="linear(to-br, rgba(91, 13, 22,0.15), rgba(91, 13, 22,0.05))">
                <MdAssignment color={C.secondary} size={48} />
              </CurriculumCardMedia>
              <CurriculumCardBody>
                <Text fontSize="sm" fontWeight="800" color={C.secondary} noOfLines={2} mb={1}>
                  {itemTitle(ex)}
                </Text>
                <Text
                  fontSize="10px"
                  fontWeight="700"
                  color={`${C.secondary}99`}
                  textTransform="uppercase"
                  letterSpacing="0.06em"
                  mb={examDone ? 1 : 3}
                >
                  {formatMetaLine(ex, "exam")}
                </Text>
                {examDone && scoreDenom != null && String(scoreDenom).trim() !== "" ? (
                  <Text fontSize="xs" fontWeight="700" color={C.muted} mb={3}>
                    Your score: {ex.right_answers} / {scoreDenom}
                  </Text>
                ) : examDone ? (
                  <Text fontSize="xs" fontWeight="700" color={C.muted} mb={3}>
                    Completed
                  </Text>
                ) : null}
                <Box mt="auto">
                  <VStack spacing={2} align="stretch">
                    {examDone ? (
                      canReviewAnswers ? (
                        <Button
                          as={Link}
                          to={examResultPath}
                          w="full"
                          size="sm"
                          bg={C.secondary}
                          color="white"
                          fontWeight="800"
                          textTransform="uppercase"
                          fontSize="10px"
                          borderRadius="lg"
                          _hover={{ bg: C.primary }}
                        >
                          See your answers
                        </Button>
                      ) : (
                        <Button
                          w="full"
                          size="sm"
                          bg={C.secondary}
                          color="white"
                          fontWeight="800"
                          textTransform="uppercase"
                          fontSize="10px"
                          borderRadius="lg"
                          isDisabled
                          cursor="not-allowed"
                          _disabled={{ bg: C.secondary, opacity: 0.55 }}
                        >
                          See your answers
                        </Button>
                      )
                    ) : (
                      <Button
                        as={Link}
                        to={examGuidePath}
                        w="full"
                        size="sm"
                        bg={C.secondary}
                        color="white"
                        fontWeight="800"
                        textTransform="uppercase"
                        fontSize="10px"
                        borderRadius="lg"
                        _hover={{ bg: C.primary }}
                      >
                        Start quiz
                      </Button>
                    )}

                    {ex?.redo_flag == 1 ? (
                      <Button
                        w="full"
                        size="sm"
                        variant="outline"
                        borderWidth="2px"
                        borderColor={C.secondary}
                        color={C.secondary}
                        fontWeight="800"
                        textTransform="uppercase"
                        fontSize="10px"
                        borderRadius="lg"
                        isLoading={redoLoadingExamId === ex?.id}
                        onClick={() => {
                          setRedoLoadingExamId(ex?.id);
                          const formData = new FormData();
                          formData.append("exam_id", ex?.id);
                          const action = () => setRedoLoadingExamId(null);
                          dispatch(examRedoRequest(formData, action));
                        }}
                        _hover={{ bg: `${C.secondary}12` }}
                      >
                        Request Redo
                      </Button>
                    ) : null}
                  </VStack>
                </Box>
              </CurriculumCardBody>
            </CurriculumCard>
          );
        })}

        {liveVideos.map((lv) => {
          const join =
            lv.join_url || lv.url || lv.link || lv.zoom_link || lv.meeting_url;
          const isLiveActive = Number(lv.active) === 1;
          const liveNow = lv.is_live === 1 || lv.is_live === true || lv.status === "live";
          const liveAttendanceId = lv.zoom_id ?? lv.id;
          return (
            <CurriculumCard key={`lv-${lv.id}`}>
              <CurriculumCardMedia
                badge="Live session"
                hero="linear(to-br, rgba(91, 13, 22,0.2), rgba(91, 13, 22,0.1))"
                topRight={
                  liveNow ? (
                    <Box
                      bg="red.600"
                      color="white"
                      fontSize="9px"
                      fontWeight="800"
                      textTransform="uppercase"
                      letterSpacing="0.06em"
                      px={2}
                      py={0.5}
                      borderRadius="full"
                    >
                      Live now
                    </Box>
                  ) : null
                }
              >
                <MdCalendarMonth color={C.primary} size={48} />
              </CurriculumCardMedia>
              <CurriculumCardBody>
                <Text fontSize="sm" fontWeight="800" color={C.primary} noOfLines={2} mb={1}>
                  {itemTitle(lv)}
                </Text>
                <Text fontSize="10px" fontWeight="700" color={`${C.primary}99`} textTransform="uppercase" letterSpacing="0.06em" mb={3}>
                  {formatMetaLine(lv, "live")}
                </Text>
                <Box mt="auto">
                  <Button
                    w="full"
                    size="sm"
                    bg={C.secondary}
                    color="white"
                    fontWeight="800"
                    textTransform="uppercase"
                    fontSize="10px"
                    borderRadius="lg"
                    isDisabled={!join || !isLiveActive}
                    _hover={{ bg: C.primary, textDecoration: "none" }}
                    onClick={() => {
                      if (!isLiveActive || !join) return;
                      if (liveAttendanceId != null) {
                        dispatch(setLiveVideoSeenRequest(liveAttendanceId));
                      }
                      window.open(join, "_blank", "noopener,noreferrer");
                    }}
                  >
                    Join now
                  </Button>
                </Box>
              </CurriculumCardBody>
            </CurriculumCard>
          );
        })}

        {homeworks.map((hw) => {
          const statusLine = homeworkStatusSummary(hw);
          const sh = hw?.student_homework;
          const label = homeworkActionLabel(hw);
          const disabled = homeworkDisabled(hw);
          return (
            <CurriculumCard key={`hw-${hw.id}`}>
              <CurriculumCardMedia
                badge="Homework"
                badgeTone="homework"
                hero="linear(to-br, rgba(168,160,150,0.2), rgba(91, 13, 22,0.08))"
              >
                <MdHomeWork color={C.primary} size={48} />
              </CurriculumCardMedia>
              <CurriculumCardBody>
                <Text fontSize="sm" fontWeight="800" color={C.primary} noOfLines={2} mb={1}>
                  {itemTitle(hw)}
                </Text>
                <Text
                  fontSize="10px"
                  fontWeight="700"
                  color={`${C.primary}99`}
                  textTransform="uppercase"
                  letterSpacing="0.06em"
                  mb={statusLine || sh?.evaluation || sh?.comment ? 2 : 3}
                >
                  {formatMetaLine(hw, "homework")}
                </Text>
                {statusLine ? (
                  <Text fontSize="xs" fontWeight="700" color={C.muted} mb={1}>
                    {statusLine}
                  </Text>
                ) : null}
                {sh?.evaluation ? (
                  <Text fontSize="11px" color={C.muted} noOfLines={3} mb={1}>
                    Evaluation: {sh.evaluation}
                  </Text>
                ) : null}
                {sh?.comment ? (
                  <Text fontSize="11px" color={C.muted} noOfLines={3} mb={3}>
                    Comment: {sh.comment}
                  </Text>
                ) : statusLine ? (
                  <Box mb={3} />
                ) : null}
                <Box mt="auto">
                  <Button
                    w="full"
                    size="sm"
                    bg={C.secondary}
                    color="white"
                    fontWeight="800"
                    textTransform="uppercase"
                    fontSize="10px"
                    borderRadius="lg"
                    isDisabled={disabled}
                    _hover={disabled ? {} : { bg: C.primary }}
                    onClick={() => handleHomeworkPrimary(hw)}
                  >
                    {label}
                  </Button>
                </Box>
              </CurriculumCardBody>
            </CurriculumCard>
          );
        })}
      </Grid>
      <SolveHomework
        data={homeworkForModal}
        setSelectedData={setHomeworkForModal}
        onSubmitted={() => {
          if (chapter?.id) dispatch(getChapterContentRequest(chapter.id));
        }}
      />
    </Box>
  );
};

/** Cream tints on maroon hero — `C.border` / `--border-subtle` is too faint here in dark mode. */
const HERO_OVERVIEW = {
  border: "rgba(247, 242, 236, 0.32)",
  icon: "rgba(247, 242, 236, 0.78)",
  label: "rgba(247, 242, 236, 0.78)",
  eyebrow: "rgba(247, 242, 236, 0.65)",
};

const StatTile = ({ icon: Icon, label, value }) => (
  <Flex bg={C.primary} p={4} align="center" gap={4} borderWidth="1px" borderColor={HERO_OVERVIEW.border}>
    <Box color={HERO_OVERVIEW.icon} flexShrink={0}>
      <Icon size={22} />
    </Box>
    <Box>
      <Text fontWeight="800" color={C.cream} fontSize="lg" lineHeight={1}>
        {value}
      </Text>
      <Text
        fontSize="10px"
        fontWeight="800"
        color={HERO_OVERVIEW.label}
        textTransform="uppercase"
        letterSpacing="0.08em"
      >
        {label}
      </Text>
    </Box>
  </Flex>
);

const StudentCourseDetails = () => {
  const { courseId } = useParams();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const sections = useSelector((s) => s.sections.sections);
  const sectionChapters = useSelector((s) => s.sections.section);
  const sectionsLoading = useSelector((s) => s.sections.isLoading);
  const chapterContentById = useSelector((s) => s.studentChapters.chapterContentById);
  const userData = useSelector((s) => s.user.userData);

  const [openIdx, setOpenIdx] = useState([]);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [activeVideoId, setActiveVideoId] = useState(null);
  const [activeVideo, setActiveVideo] = useState(null);
  const [modalChapterLabel, setModalChapterLabel] = useState("");
  const [pdfPreview, setPdfPreview] = useState(null);

  const openVideoModal = (video, chapterLabel) => {
    setActiveVideo(video ?? null);
    setActiveVideoId(video?.id ?? null);
    setModalChapterLabel(chapterLabel || "");
    setVideoModalOpen(true);
  };

  const closeVideoModal = () => {
    setVideoModalOpen(false);
    setActiveVideoId(null);
    setActiveVideo(null);
    setModalChapterLabel("");
  };

  const meta = useMemo(() => {
    const raw = sections;
    const list = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
    return list.find((x) => String(x.id) === String(courseId)) || null;
  }, [sections, courseId]);

  const chapters = useMemo(() => {
    const ch = sectionChapters;
    return Array.isArray(ch) ? ch : Array.isArray(ch?.data) ? ch.data : [];
  }, [sectionChapters]);

  useEffect(() => {
    setOpenIdx([]);
  }, [courseId]);

  useEffect(() => {
    dispatch(getAllSectionsRequest());
  }, [dispatch]);

  useEffect(() => {
    if (courseId) dispatch(getSectionRequest(courseId));
  }, [dispatch, courseId]);

  const ensureContent = useCallback(
    (indices) => {
      indices.forEach((i) => {
        const ch = chapters[i];
        if (!ch?.id) return;
        const bucket = chapterContentById[ch.id];
        if (!bucket?.loaded && !bucket?.loading) {
          dispatch(getChapterContentRequest(ch.id));
        }
      });
    },
    [chapters, chapterContentById, dispatch]
  );

  useEffect(() => {
    const chapterParam = searchParams.get("chapter");
    if (!chapterParam || chapters.length === 0) return;
    const idx = chapters.findIndex((ch) => String(ch.id) === String(chapterParam));
    if (idx < 0) return;
    setOpenIdx((prev) => (prev.includes(idx) ? prev : [...prev, idx]));
    ensureContent([idx]);
  }, [searchParams, chapters, ensureContent]);

  const handleAccordionChange = (expandedIndex) => {
    const next = Array.isArray(expandedIndex)
      ? expandedIndex
      : expandedIndex === -1
        ? []
        : [expandedIndex];
    setOpenIdx(next);
    ensureContent(next);
  };

  const expandAll = () => {
    const all = chapters.map((_, i) => i);
    setOpenIdx(all);
    ensureContent(all);
  };

  const img = publicAssetUrl(meta?.image);
  const progress = meta?.progress ?? meta?.completion_percent ?? 0;
  const lessonsCount = meta?.lessons_count ?? chapters.length;
  const videosCount = meta?.videos_count ?? 0;
  const pdfsCount = meta?.pdfs_count ?? 0;
  const examsCount = meta?.exams_count ?? 0;
  const liveCount = meta?.live_videos_count ?? meta?.live_count ?? 0;

  return (
    <StudentPortalShell>
      <Flex direction="column" minH={{ md: "100vh" }}>
        <Box bg={C.primary} w="100%" py={{ base: 10, md: 16 }} px={{ base: 4, md: 12 }} borderBottomWidth="1px" borderColor={C.border}>
          <Flex
            direction={{ base: "column", md: "row" }}
            align={{ base: "stretch", md: "center" }}
            maxW="5xl"
            w="100%"
            gap={{ base: 8, md: 12 }}
          >
            <Box w={{ base: "100%", md: "42%" }} flexShrink={0}>
              {img ? (
                <Image
                  src={img}
                  alt={meta?.name || "Course"}
                  w="100%"
                  aspectRatio={1}
                  objectFit="cover"
                  borderWidth="4px"
                  borderColor="rgba(247,242,236,0.12)"
                  borderRadius="xl"
                  boxShadow={`8px 8px 0 0 ${C.secondary}`}
                />
              ) : (
                <Flex
                  aspectRatio={1}
                  borderRadius="xl"
                  bg="rgba(247,242,236,0.08)"
                  align="center"
                  justify="center"
                  color={C.cream}
                  fontWeight="900"
                  fontSize="4xl"
                  borderWidth="4px"
                  borderColor="rgba(247,242,236,0.12)"
                >
                  {(meta?.name || "?").slice(0, 2).toUpperCase()}
                </Flex>
              )}
            </Box>
            <Box flex={1} color={C.cream}>
              <Text
                fontSize="xs"
                fontWeight="800"
                letterSpacing="0.2em"
                textTransform="uppercase"
                color={HERO_OVERVIEW.eyebrow}
                mb={2}
              >
                Course overview
              </Text>
              <Heading
                as="h1"
                fontSize={{ base: "2xl", md: "4xl" }}
                fontWeight="800"
                color={C.warmBeige}
                textTransform="uppercase"
                letterSpacing="tight"
                lineHeight="1.15"
                mb={6}
              >
                {meta?.name || "Course"}
              </Heading>

              <SimpleGrid
                columns={2}
                spacing={0}
                borderWidth="1px"
                borderColor={HERO_OVERVIEW.border}
                borderRadius="xl"
                overflow="hidden"
                mb={6}
              >
                <StatTile icon={MdMenuBook} label="Sessions" value={meta?.sessions_count ?? videosCount ?? lessonsCount} />
                <StatTile icon={MdVideoLibrary} label="Live sessions" value={meta?.live_videos_count ?? liveCount} />
                <StatTile icon={MdDescription} label="PDFs" value={pdfsCount} />
                <StatTile icon={MdQuiz} label="Exams" value={examsCount} />
              </SimpleGrid>

            </Box>
          </Flex>
        </Box>

        <Box maxW="4xl" mx="auto" py={{ base: 10, md: 16 }} px={{ base: 4, md: 6 }} w="100%">
          <Flex align="center" justify="space-between" borderBottomWidth="2px" borderColor={C.primary} pb={4} mb={10} flexWrap="wrap" gap={4}>
            <Heading fontSize="2xl" fontWeight="800" color={C.primary} textTransform="uppercase" letterSpacing="tight">
              Curriculum content
            </Heading>
          </Flex>

          {sectionsLoading && chapters.length === 0 ? (
            <VStack spacing={4} align="stretch">
              {[1, 2, 3].map((k) => (
                <Skeleton key={k} h="72px" borderRadius="xl" />
              ))}
            </VStack>
          ) : chapters.length === 0 ? (
            <Text color={C.muted} fontWeight="600">
              No lessons are published for this course yet.
            </Text>
          ) : (
            <Accordion allowMultiple index={openIdx} onChange={handleAccordionChange}>
              {chapters.map((chapter, index) => (
                <AccordionItem
                  key={chapter.id}
                  bg={studentSurfaces.raised}
                  borderWidth="1px"
                  borderColor={C.border}
                  borderRadius="xl"
                  mb={4}
                  overflow="hidden"
                >
                  <AccordionButton
                    px={6}
                    py={5}
                    _hover={{ bg: studentSurfaces.muted }}
                    borderBottomWidth="1px"
                    borderColor={C.border}
                  >
                    <Flex flex={1} align="center" gap={4} textAlign="left">
                      <Text fontWeight="800" fontSize="2xl" color={C.primary} opacity={0.2} minW="36px">
                        {String(index + 1).padStart(2, "0")}
                      </Text>
                      <Text flex={1} fontWeight="700" fontSize="md" color={C.primary}>
                        {chapter.name || `Lesson ${index + 1}`}
                      </Text>
                      {chapterContentById[chapter.id]?.loading ? <Spinner size="sm" color={C.primary} /> : null}
                    </Flex>
                    <AccordionIcon color={C.primary} />
                  </AccordionButton>
                  <AccordionPanel p={0}>
                    <ChapterContentGrid
                      bucket={chapterContentById[chapter.id]}
                      chapter={chapter}
                      onWatchVideo={openVideoModal}
                      userData={userData}
                      courseReturnPath={courseId ? `/students/courses/${courseId}` : ""}
                      onOpenPdf={setPdfPreview}
                    />
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          )}

          <Flex
            mt={16}
            pt={8}
            borderTopWidth="1px"
            borderColor={C.border}
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            gap={8}
            align={{ base: "flex-start", md: "center" }}
          >
            <Flex gap={{ base: 6, md: 12 }} flexWrap="wrap">
              {meta?.created_at || meta?.enrolled_at ? (
                <Box>
                  <Text fontSize="10px" fontWeight="800" color={`${C.primary}66`} textTransform="uppercase" letterSpacing="0.1em" mb={1}>
                    Enrolled
                  </Text>
                  <Text fontWeight="700" color={C.primary} fontSize="sm">
                    {meta.enrolled_at || meta.created_at}
                  </Text>
                </Box>
              ) : null}
              {meta?.expected_end || meta?.ends_at ? (
                <Box>
                  <Text fontSize="10px" fontWeight="800" color={`${C.primary}66`} textTransform="uppercase" letterSpacing="0.1em" mb={1}>
                    Expected end
                  </Text>
                  <Text fontWeight="700" color={C.primary} fontSize="sm">
                    {meta.expected_end || meta.ends_at}
                  </Text>
                </Box>
              ) : null}
              {meta?.teacher_name || meta?.instructor_name ? (
                <Box>
                  <Text fontSize="10px" fontWeight="800" color={`${C.primary}66`} textTransform="uppercase" letterSpacing="0.1em" mb={1}>
                    Instructor
                  </Text>
                  <Text fontWeight="700" color={C.primary} fontSize="sm" textDecor="underline" textUnderlineOffset={4}>
                    {meta.teacher_name || meta.instructor_name}
                  </Text>
                </Box>
              ) : null}
            </Flex>
            <Button
              as={ChakraLink}
              href={meta?.syllabus_url || meta?.syllabus_pdf}
              isDisabled={!(meta?.syllabus_url || meta?.syllabus_pdf)}
              bg={C.primary}
              color="white"
              fontWeight="800"
              fontSize="xs"
              textTransform="uppercase"
              letterSpacing="0.12em"
              px={8}
              py={6}
              borderRadius="xl"
              _hover={{ bg: C.secondary, textDecoration: "none" }}
            >
              Course syllabus PDF
            </Button>
          </Flex>
        </Box>
      </Flex>
      <CourseVideoModal
        isOpen={Boolean(videoModalOpen && activeVideoId)}
        onClose={closeVideoModal}
        chapterLabel={modalChapterLabel}
        videoId={activeVideoId}
        initialVideoFiles={activeVideo?.files}
        videoTitleFallback={activeVideo?.title}
        onOpenPdf={setPdfPreview}
      />
      <PdfPreviewModal
        isOpen={Boolean(pdfPreview?.url)}
        onClose={() => setPdfPreview(null)}
        pdfUrl={pdfPreview?.url || ""}
        title={pdfPreview?.title}
        backLabel={pdfPreview?.backLabel}
        totalPages={pdfPreview?.totalPages}
      />
      <AddVoucher />
    </StudentPortalShell>
  );
};

export default StudentCourseDetails;
